// accept_cycle (PH; WF-013, CORE-057/058): the PH's declarations before work starts on
// this cycle. The server checks validation, gas, toolbox talk, stops and capacity.
import { useId, useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Badge, Banner, Field, KeyValue, Select } from '@/components/ui'
import { ACCEPTANCE_DECLARATIONS } from '@/domain/catalogue'
import type { ActionPanelProps } from '@/features/contracts'
import { fmtDateTime } from '@/lib/format'
import { PanelFrame } from '../frame'
import { usePanelCommand } from '../hooks'
import {
  actionArg,
  allTicked,
  cycleForAction,
  gasPlanOf,
  latestTest,
  observedInPeriod,
  revisionOf,
  speciesLabel,
  talkSignatures,
  talksNewestFirst,
  tickProblems,
} from '../logic'
import { CycleHeader, GasClassificationBadge, MissingCycle, SignedState, SigningStatement, TickList, WindowText } from '../parts'

export function AcceptCyclePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cycle = cycleForAction(bundle, action)
  const run = usePanelCommand('accept_cycle', bundle, onDone)
  const revision = revisionOf(bundle, cycle)
  const gasPlan = gasPlanOf(revision)
  const talks = cycle ? talksNewestFirst(cycle.toolbox_talks) : []
  const [talkId, setTalkId] = useState(() => actionArg(action, 'toolbox_talk_id') ?? talks[0]?.id ?? '')
  const [declarations, setDeclarations] = useState<Record<string, boolean>>({})
  const [tried, setTried] = useState(false)
  const talkSelectId = useId()

  // Only a talk still listed for this cycle is sent.
  const talk = talks.find((t) => t.id === talkId) ?? null
  const latest = cycle ? latestTest(cycle) : null
  const v = cycle?.validation ?? null

  const problems: string[] = []
  if (!talk) problems.push('Choose the toolbox talk for this cycle.')
  problems.push(...tickProblems(ACCEPTANCE_DECLARATIONS, declarations, 'declaration'))

  function submit() {
    setTried(true)
    if (!cycle || !talk || problems.length > 0) return
    void run.submit({
      permit_id: bundle.permit.id,
      cycle_id: cycle.id,
      toolbox_talk_id: talk.id,
      declarations: allTicked(ACCEPTANCE_DECLARATIONS, declarations),
    })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Sign acceptance"
      blockedNote={cycle ? null : 'Cycle not found.'}
    >
      {cycle ? <CycleHeader cycle={cycle} /> : <MissingCycle />}

      {v ? (
        <div className="mb-4 rounded-lg border border-line bg-white p-3">
          <KeyValue
            items={[
              ['Validated by', `${v.attestation.signer.display_name}, ${fmtDateTime(v.attestation.server_signed_at)}`],
              ['Valid', <WindowText from={v.validation_from} to={v.validation_to} />],
            ]}
          />
          <p className="mt-2 text-xs text-ink-soft">You can accept only inside this validation period.</p>
        </div>
      ) : cycle ? (
        <Banner tone="warn" title="Not validated" className="mb-4">
          The RS has not validated this cycle.
        </Banner>
      ) : null}

      <section className="mb-4">
        <h3 className="display mb-2 text-base font-semibold text-ink">
          Gas test before work
          <RuleRefs ruleIds={['CORE-038', 'CORE-055']} decisionIds={['DEC-CORE-005']} />
        </h3>
        {gasPlan ? (
          <>
            <Banner tone="warn" title="Pre-arranged gas tests are not enough to start" className="mb-3">
              An acceptable gas test must be recorded in this cycle, inside the validated period, before you accept. A test taken
              before the validated period, or one the RS only pre-arranged at validation, does not count. (This order is an engineering choice for
              the demo, blueprint WF-013.)
            </Banner>
            <p className="mb-1 text-sm text-ink">Gas plan: {gasPlan.species.map(speciesLabel).join(', ') || 'no gases listed'}.</p>
            {v?.gas_condition === 'prearranged' ? (
              <p className="mb-1 text-sm text-ink">The RS validated this cycle with the gas test pre-arranged.</p>
            ) : null}
            <div className="rounded-lg border border-line bg-white p-3 text-sm">
              {latest ? (
                <div className="space-y-1">
                  <p className="text-ink">Latest test in this cycle, observed {fmtDateTime(latest.reported_observed_at)}</p>
                  <p className="text-ink-soft">Sample location: {latest.sample_location}</p>
                  <GasClassificationBadge test={latest} />
                  {v && observedInPeriod(latest, v) === false ? (
                    <p className="font-semibold text-warn">
                      Taken outside the validated period, so it does not count for starting work. Ask the AGT for a new test.
                    </p>
                  ) : null}
                </div>
              ) : (
                <span className="font-semibold text-warn">No gas test recorded in this cycle yet.</span>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-ink-soft">The permit has no gas testing requirement.</p>
        )}
      </section>

      <section className="mb-4">
        <h3 className="display mb-2 text-base font-semibold text-ink">
          Toolbox talk
          <RuleRefs ruleIds={['CORE-058', 'CORE-060']} />
        </h3>
        {talks.length === 0 ? (
          <Banner tone="warn" title="No toolbox talk on this cycle">
            Give the toolbox talk and get every attendee&apos;s own signature first.
          </Banner>
        ) : (
          <>
            <Field label="Toolbox talk for this cycle" required htmlFor={talkSelectId}>
              <Select
                id={talkSelectId}
                value={talk?.id ?? ''}
                onChange={(e) => setTalkId(e.target.value)}
                placeholder="Choose the talk"
                options={talks.map((t) => {
                  const s = talkSignatures(t)
                  return {
                    value: t.id,
                    label: `${s.signed} of ${s.total} signed - given ${fmtDateTime(t.reported_conducted_at)} by ${t.giver.signer.display_name}`,
                  }
                })}
              />
            </Field>
            {talk ? (
              <div className="rounded-lg border border-line bg-white p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-ink">Attendees</p>
                  {talk.complete ? (
                    <Badge tone="ok">
                      <span aria-hidden>&#10003;</span>All signed
                    </Badge>
                  ) : (
                    <Badge tone="warn">
                      {talkSignatures(talk).signed} of {talkSignatures(talk).total} signed
                    </Badge>
                  )}
                </div>
                <ul className="divide-y divide-line text-sm">
                  {talk.attendees.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <span className="text-ink">{a.person.display_name}</span>
                      <SignedState a={a.signed} />
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-ink-soft">Every attendee must sign their own understanding before you accept.</p>
              </div>
            ) : null}
          </>
        )}
      </section>

      <TickList
        title="Your declarations"
        ruleIds={['CORE-057', 'CORE-058']}
        options={ACCEPTANCE_DECLARATIONS}
        value={declarations}
        onChange={setDeclarations}
      />
      <p className="-mt-2 mb-4 text-xs text-ink-soft">If any statement is not true, do not sign. Refuse to accept an unsafe site.</p>

      {cycle ? (
        <SigningStatement role="PH" person={bundle.viewer.person}>
          <p>I accept cycle {cycle.cycle_number}. Each statement I ticked is true now.</p>
          <p>I am satisfied the site is safe to start. I will keep the work in sight and in communication, and stop it if anything changes.</p>
          <p>This acceptance is for cycle {cycle.cycle_number} only.</p>
        </SigningStatement>
      ) : null}
    </PanelFrame>
  )
}
